import { apiClient } from './api-client';
import { isMockMode, mockDelay } from './mock-mode';
import {
  DEFAULT_EMAIL_TEMPLATE_BODY,
  DEFAULT_EMAIL_TEMPLATE_SUBJECT,
  fixtureBotKnowledge,
  fixtureChannels,
  fixtureConversationMessages,
  fixtureConversations,
  fixtureCustomers,
  fixtureDashboardSummary,
  fixtureLeadActivity,
  fixtureLeads,
  fixtureSupportRequests,
  fixtureTickets,
  mockAssistantReply,
  mockBotConversation,
  mockBotGaps,
  mockBotPreviewReply,
  mockBotUsage,
} from './fixtures';
import type {
  SentEmailLog,
  SentEmailsQuery,
  SentEmailsResult,
  AssistantMessage,
  AssistantReply,
  BotConfig,
  BotConversationHistory,
  BotDomains,
  BotIntro,
  BotKnowledge,
  BotKnowledgeGap,
  BotPreviewReply,
  BotUsage,
  Channel,
  Conversation,
  ConversationMessage,
  CreateChannelInput,
  Customer,
  DashboardSummary,
  EmailBrand,
  EmailTemplatesResponse,
  EmailTemplateType,
  GmailMessageSummary,
  Lead,
  LeadActivity,
  PreviewEmailTemplateInput,
  PreviewTransactionalEmailInput,
  RenderedEmail,
  RenderedTransactionalEmail,
  SaveTransactionalEmailInput,
  SupportRequest,
  Ticket,
  TransactionalEmailTemplate,
  NotificationRecipient,
  UpdateChannelInput,
  UpsertEmailTemplateInput,
} from '@/types/engage';

/**
 * Service module — every endpoint that the engage frontend expects from
 * zafroi-mvp-runtime. In mock mode (default in dev) these resolve from
 * fixtures; in live mode they hit `/api/v1/engage/*` on the runtime.
 *
 * See `docs/BACKEND_CONTRACT.md` for the authoritative request/response shapes.
 */

/** Runtime list endpoints page their results. `api-client` has already
 * unwrapped the `{ success, data, meta }` envelope, so this is `data`. */
interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

// ─── Dashboard ─────────────────────────────────────────────────────────────
export async function getDashboardSummary(): Promise<DashboardSummary> {
  if (isMockMode) return mockDelay(fixtureDashboardSummary);
  const { data } = await apiClient.get<DashboardSummary>('/engage/dashboard/summary');
  return data;
}

// ─── Leads ─────────────────────────────────────────────────────────────────
export async function listLeads(): Promise<Lead[]> {
  if (isMockMode) return mockDelay(fixtureLeads);
  const { data } = await apiClient.get<Paginated<Lead>>('/engage/leads');
  return data.items;
}

export async function getLead(leadId: string): Promise<Lead | null> {
  if (isMockMode) {
    return mockDelay(fixtureLeads.find((l) => l.id === leadId) ?? null);
  }
  const { data } = await apiClient.get<Lead>(`/engage/leads/${leadId}`);
  return data;
}

export async function listLeadActivity(leadId: string): Promise<LeadActivity[]> {
  if (isMockMode) return mockDelay(fixtureLeadActivity[leadId] ?? []);
  const { data } = await apiClient.get<LeadActivity[]>(
    `/engage/leads/${leadId}/activity`,
  );
  return data;
}

// ─── Customers ─────────────────────────────────────────────────────────────
export async function listCustomers(): Promise<Customer[]> {
  if (isMockMode) return mockDelay(fixtureCustomers);
  const { data } = await apiClient.get<Paginated<Customer>>('/engage/customers');
  return data.items;
}

export async function getCustomer(customerId: string): Promise<Customer | null> {
  if (isMockMode) {
    return mockDelay(fixtureCustomers.find((c) => c.id === customerId) ?? null);
  }
  const { data } = await apiClient.get<Customer>(`/engage/customers/${customerId}`);
  return data;
}

// ─── Conversations ─────────────────────────────────────────────────────────
export async function listConversations(): Promise<Conversation[]> {
  if (isMockMode) return mockDelay(fixtureConversations);
  const { data } = await apiClient.get<Paginated<Conversation>>(
    '/engage/conversations',
  );
  return data.items;
}

export async function getConversation(
  conversationId: string,
): Promise<Conversation | null> {
  if (isMockMode) {
    return mockDelay(
      fixtureConversations.find((c) => c.id === conversationId) ?? null,
    );
  }
  const { data } = await apiClient.get<Conversation>(
    `/engage/conversations/${conversationId}`,
  );
  return data;
}

export async function listConversationMessages(
  conversationId: string,
): Promise<ConversationMessage[]> {
  if (isMockMode) {
    return mockDelay(fixtureConversationMessages[conversationId] ?? []);
  }
  const { data } = await apiClient.get<ConversationMessage[]>(
    `/engage/conversations/${conversationId}/messages`,
  );
  return data;
}

// ─── Support requests ──────────────────────────────────────────────────────
export async function listSupportRequests(): Promise<SupportRequest[]> {
  if (isMockMode) return mockDelay(fixtureSupportRequests);
  const { data } = await apiClient.get<Paginated<SupportRequest>>(
    '/engage/support',
  );
  return data.items;
}

export async function getSupportRequest(
  requestId: string,
): Promise<SupportRequest | null> {
  if (isMockMode) {
    return mockDelay(
      fixtureSupportRequests.find((s) => s.id === requestId) ?? null,
    );
  }
  const { data } = await apiClient.get<SupportRequest>(
    `/engage/support/${requestId}`,
  );
  return data;
}

/**
 * Session-only in-memory store of conversation messages keyed by support
 * request id. Lazily seeded on first read so the agent sees a plausible
 * starting thread (customer email + auto-respond + maybe an agent reply)
 * for the fixture requests. Replies push synthetic outbound messages here
 * and the resolve flow leaves it alone — same pattern as `mockChannels`
 * and `mockBotKnowledge` above.
 */
const mockSupportMessages: Record<string, ConversationMessage[]> = {};

/** Seed a plausible starter thread for a fixture request the first time
 * the agent opens it. Only seeded for EMAIL-channel requests — non-email
 * fixtures legitimately have no thread, which exercises the empty state. */
function ensureMockSupportThread(requestId: string): ConversationMessage[] {
  const existing = mockSupportMessages[requestId];
  if (existing) return existing;
  const request = fixtureSupportRequests.find((s) => s.id === requestId);
  if (!request) {
    mockSupportMessages[requestId] = [];
    return mockSupportMessages[requestId];
  }
  if (request.channel !== 'email') {
    mockSupportMessages[requestId] = [];
    return mockSupportMessages[requestId];
  }
  const customer = fixtureCustomers.find((c) => c.id === request.customerId);
  // Anchor the seeded thread a little before the request was created so the
  // ordering reads naturally — customer wrote in, auto-respond fired back,
  // then the request landed in the queue.
  const createdAt = new Date(request.createdAt).getTime();
  const t = (offsetMs: number) => new Date(createdAt + offsetMs).toISOString();
  const seed: ConversationMessage[] = [
    {
      id: `srm_${request.id}_1`,
      direction: 'inbound',
      authorName: customer?.name ?? request.customerName,
      body:
        request.description ??
        `Hi team — ${request.subject.toLowerCase()}. Could you take a look when you get a chance? Thanks.`,
      channel: 'email',
      at: t(-1000 * 60 * 8),
    },
    {
      id: `srm_${request.id}_2`,
      direction: 'outbound',
      // Auto-respond — distinguishable from a human agent by authorName.
      authorName: 'Auto-respond',
      body:
        "Thanks for getting in touch — we've received your request and a team " +
        "member will be back to you shortly.",
      channel: 'email',
      at: t(-1000 * 60 * 7),
    },
  ];
  // For requests already in progress, give the agent a head start so the
  // thread shows there's been some back-and-forth already.
  if (request.status === 'in_progress') {
    seed.push({
      id: `srm_${request.id}_3`,
      direction: 'outbound',
      authorName: 'Priya Iyer',
      body:
        `Hi ${customer?.name?.split(' ')[0] ?? 'there'} — ` +
        "thanks for the note. I'm looking into this now and will follow up " +
        'shortly with next steps.',
      channel: 'email',
      at: t(1000 * 60 * 60 * 2),
    });
  }
  mockSupportMessages[request.id] = seed;
  return seed;
}

export async function getSupportMessages(
  id: string,
): Promise<{ messages: ConversationMessage[] }> {
  if (isMockMode) {
    const messages = ensureMockSupportThread(id);
    // Return a shallow copy so callers can't mutate the store accidentally.
    return mockDelay({ messages: [...messages] });
  }
  const { data } = await apiClient.get<{ messages: ConversationMessage[] }>(
    `/engage/support/${id}/messages`,
  );
  return data;
}

/**
 * Send an agent reply on a support request. EMAIL channel only on the BE —
 * other channels return a clear "not yet supported" error, as does a request
 * with no conversation thread (legacy/synthetic) or a customer without an
 * email address. The runtime persists the outbound message, posts it via
 * Gmail, and flips PENDING -> IN_PROGRESS on the first agent reply.
 */
export async function replyToSupportRequest(
  id: string,
  body: string,
): Promise<{ request: SupportRequest; message: ConversationMessage }> {
  if (isMockMode) {
    const idx = fixtureSupportRequests.findIndex((s) => s.id === id);
    const existing = idx >= 0 ? fixtureSupportRequests[idx] : null;
    if (!existing) {
      throw new Error('Support request not found.');
    }
    if (existing.channel !== 'email') {
      throw new Error(
        'Replies via this channel are not supported yet — only email replies can be sent for now.',
      );
    }
    const now = new Date().toISOString();
    const message: ConversationMessage = {
      id: `srm_${id}_${Date.now()}`,
      direction: 'outbound',
      authorName: 'You',
      body,
      channel: 'email',
      at: now,
      createdAt: now,
    };
    ensureMockSupportThread(id);
    mockSupportMessages[id] = [...(mockSupportMessages[id] ?? []), message];
    // First agent reply flips PENDING/TRIAGED to IN_PROGRESS — mirror the BE.
    const nextStatus: SupportRequest['status'] =
      existing.status === 'pending' || existing.status === 'triaged'
        ? 'in_progress'
        : existing.status;
    const updated: SupportRequest = {
      ...existing,
      status: nextStatus,
      updatedAt: now,
    };
    // Mutate in place so subsequent list/detail refetches see the new state.
    fixtureSupportRequests[idx] = updated;
    return mockDelay({ request: updated, message });
  }
  const { data } = await apiClient.post<{
    request: SupportRequest;
    message: ConversationMessage;
  }>(`/engage/support/${id}/reply`, { body });
  return data;
}

/**
 * Resolve a support request — Loop B of the self-improving knowledge base.
 * The runtime marks the request `resolved`, then runs an LLM pass that drafts
 * a reusable knowledge-base article from the issue + resolution and stores it
 * as a DRAFT pending tenant review. That draft pass makes this call SLOW
 * (~15–20s), hence the generous timeout. `suggestedDraft` is `null` when the
 * drafting step failed — the resolve itself still succeeded.
 */
export async function resolveSupportRequest(
  id: string,
  resolutionNotes: string,
): Promise<{ request: SupportRequest; suggestedDraft: BotKnowledge | null }> {
  if (isMockMode) {
    const now = new Date().toISOString();
    const existing = fixtureSupportRequests.find((s) => s.id === id);
    const request: SupportRequest = {
      ...(existing ?? fixtureSupportRequests[0]),
      id,
      status: 'resolved',
      resolutionNotes,
      resolvedAt: now,
      updatedAt: now,
    };
    const suggestedDraft: BotKnowledge = {
      id: `bk_${Math.random().toString(36).slice(2, 8)}`,
      title: `How we handle: ${request.subject}`,
      content:
        `${resolutionNotes}\n\n` +
        '(AI-drafted from a resolved support request — review the wording ' +
        'before approving it for the customer-facing bot.)',
      category: 'Support resolutions',
      status: 'draft',
      suggestedFromRequestId: id,
      createdAt: now,
      updatedAt: now,
    };
    // Persist into the session knowledge store so it appears on the Chatbot
    // page's knowledge list straight away.
    mockBotKnowledge = [suggestedDraft, ...mockBotKnowledge];
    return mockDelay({ request, suggestedDraft });
  }
  const { data } = await apiClient.post<{
    request: SupportRequest;
    suggestedDraft: BotKnowledge | null;
  }>(
    `/engage/support/${id}/resolve`,
    { resolutionNotes },
    // The runtime runs an LLM draft pass — give it room to finish.
    { timeout: 120_000 },
  );
  return data;
}

// ─── Tickets ───────────────────────────────────────────────────────────────
export async function listTickets(): Promise<Ticket[]> {
  if (isMockMode) return mockDelay(fixtureTickets);
  const { data } = await apiClient.get<Paginated<Ticket>>('/engage/tickets');
  return data.items;
}

export async function getTicket(ticketId: string): Promise<Ticket | null> {
  if (isMockMode) {
    return mockDelay(fixtureTickets.find((t) => t.id === ticketId) ?? null);
  }
  const { data } = await apiClient.get<Ticket>(`/engage/tickets/${ticketId}`);
  return data;
}

// ─── Channels ──────────────────────────────────────────────────────────────

/**
 * Session-only in-memory store for mock mode. Seeded from the MAVK fixture
 * on first read and mutated by create/update/delete so the page reflects
 * edits across reloads within the same tab session — same pattern as the
 * bot-knowledge mock store above.
 */
let mockChannels: Channel[] = [...fixtureChannels];

export async function listChannels(): Promise<Channel[]> {
  if (isMockMode) return mockDelay([...mockChannels]);
  const { data } = await apiClient.get<Channel[]>('/engage/channels');
  return data;
}

export async function getChannel(id: string): Promise<Channel> {
  if (isMockMode) {
    const found = mockChannels.find((c) => c.id === id);
    if (!found) throw new Error(`Channel ${id} not found`);
    return mockDelay(found);
  }
  const { data } = await apiClient.get<Channel>(`/engage/channels/${id}`);
  return data;
}

export async function createChannel(input: CreateChannelInput): Promise<Channel> {
  if (isMockMode) {
    const now = new Date().toISOString();
    // Match the runtime default: EMAIL channels get template seeds when the
    // caller omits them — so the create flow can post just identity + label
    // and the template falls back to the system defaults.
    const seedsTemplate = input.kind === 'email';
    const entry: Channel = {
      id: `ch_${Math.random().toString(36).slice(2, 8)}`,
      kind: input.kind,
      label: input.label,
      status: 'disconnected',
      identifier: input.identifier ?? null,
      lastSyncedAt: null,
      templateSubject: seedsTemplate
        ? input.templateSubject ?? DEFAULT_EMAIL_TEMPLATE_SUBJECT
        : input.templateSubject ?? null,
      templateBody: seedsTemplate
        ? input.templateBody ?? DEFAULT_EMAIL_TEMPLATE_BODY
        : input.templateBody ?? null,
      config: input.config ?? null,
      createdAt: now,
      updatedAt: now,
    };
    mockChannels = [...mockChannels, entry];
    return mockDelay(entry);
  }
  const { data } = await apiClient.post<Channel>('/engage/channels', input);
  return data;
}

export async function updateChannel(
  id: string,
  patch: UpdateChannelInput,
): Promise<Channel> {
  if (isMockMode) {
    const now = new Date().toISOString();
    let updated: Channel | undefined;
    mockChannels = mockChannels.map((c) => {
      if (c.id !== id) return c;
      updated = {
        ...c,
        ...patch,
        // PATCH is shallow; only replace nullable fields when explicitly set.
        identifier:
          patch.identifier !== undefined ? patch.identifier ?? null : c.identifier,
        templateSubject:
          patch.templateSubject !== undefined
            ? patch.templateSubject ?? null
            : c.templateSubject,
        templateBody:
          patch.templateBody !== undefined
            ? patch.templateBody ?? null
            : c.templateBody,
        config: patch.config !== undefined ? patch.config ?? null : c.config,
        updatedAt: now,
      };
      return updated;
    });
    if (!updated) throw new Error(`Channel ${id} not found`);
    return mockDelay(updated);
  }
  const { data } = await apiClient.patch<Channel>(
    `/engage/channels/${id}`,
    patch,
  );
  return data;
}

export async function deleteChannel(id: string): Promise<void> {
  if (isMockMode) {
    mockChannels = mockChannels.filter((c) => c.id !== id);
    return mockDelay(undefined);
  }
  await apiClient.delete(`/engage/channels/${id}`);
}

// ─── Channel OAuth (Gmail) ─────────────────────────────────────────────────

/**
 * Kick off the Gmail OAuth flow for an EMAIL channel. The runtime mints a
 * Google auth URL with a signed `state` and returns it; the FE redirects the
 * full page there. Google then bounces back to
 * `/api/v1/engage/oauth/google/callback`, which finishes the dance and
 * redirects to `${ENGAGE_APP_URL}/channels?connected=<id>` or
 * `…/channels?error=<reason>&channel=<id?>`.
 *
 * Mock mode: synthesise an auth URL that loops straight back to the engage
 * app's `/channels?connected=<id>` URL so the OAuth UX can be exercised end-
 * to-end without a real Google round-trip. We also mark the channel
 * `connected` in the local mock store so the callback handler finds the
 * updated state when it refetches.
 */
export async function startGmailOAuth(
  channelId: string,
): Promise<{ authUrl: string }> {
  if (isMockMode) {
    const now = new Date().toISOString();
    mockChannels = mockChannels.map((c) =>
      c.id === channelId
        ? {
            ...c,
            status: 'connected',
            // Fake an identifier when none was set, so the connected pill has
            // something meaningful to show.
            identifier: c.identifier ?? 'support@mock.example',
            lastSyncedAt: now,
            updatedAt: now,
          }
        : c,
    );
    // Point the fake authUrl at the same-origin return URL the real flow uses,
    // so the page lands on `/channels?connected=<id>` and the callback handler
    // there picks it up — completing the full simulated round-trip.
    const authUrl = `${window.location.origin}/channels?connected=${encodeURIComponent(channelId)}`;
    return mockDelay({ authUrl });
  }
  const { data } = await apiClient.post<{ authUrl: string }>(
    `/engage/channels/${channelId}/oauth/start`,
  );
  return data;
}

/**
 * Test a Gmail channel connection by fetching the last 3 inbox messages.
 * Pure read-only check — the runtime hits Gmail with the stored credentials
 * and projects the results to a minimal summary.
 */
export async function testChannelConnection(
  channelId: string,
): Promise<GmailMessageSummary[]> {
  if (isMockMode) {
    void channelId;
    const now = Date.now();
    const samples: GmailMessageSummary[] = [
      {
        id: 'mock_m1',
        threadId: 'mock_t1',
        from: 'Hariraj Singh <hariraj@example.com>',
        subject: 'Question about my recent order',
        snippet:
          "Hi team, I wanted to check the delivery date for order #4821 — is it on track for Friday? Thanks.",
        date: new Date(now - 1000 * 60 * 23).toISOString(),
      },
      {
        id: 'mock_m2',
        threadId: 'mock_t2',
        from: 'Anita Mehra <anita.mehra@example.com>',
        subject: 'Damaged item in delivery',
        snippet:
          "Hello — one of the items arrived dented. Happy to send photos. Can someone help with a replacement?",
        date: new Date(now - 1000 * 60 * 60 * 2).toISOString(),
      },
      {
        id: 'mock_m3',
        threadId: 'mock_t3',
        from: 'Vikram Rao <vikram@example.com>',
        subject: 'Re: Invoice for May',
        snippet:
          'Thanks for the prompt response on the invoice. All looks good from our side — payment scheduled for the 28th.',
        date: new Date(now - 1000 * 60 * 60 * 18).toISOString(),
      },
    ];
    return mockDelay(samples);
  }
  const { data } = await apiClient.post<GmailMessageSummary[]>(
    `/engage/channels/${channelId}/test-connection`,
  );
  return data;
}

/**
 * Disconnect an EMAIL channel — wipes the stored Gmail credentials and
 * flips the channel back to `disconnected`. The tenant-defined template,
 * identifier and label are preserved so reconnecting later is a one-click
 * operation.
 */
export async function disconnectChannel(channelId: string): Promise<void> {
  if (isMockMode) {
    const now = new Date().toISOString();
    mockChannels = mockChannels.map((c) =>
      c.id === channelId
        ? {
            ...c,
            status: 'disconnected',
            lastSyncedAt: null,
            updatedAt: now,
          }
        : c,
    );
    return mockDelay(undefined);
  }
  await apiClient.post(`/engage/channels/${channelId}/disconnect`);
}

// ─── Branded email templates ───────────────────────────────────────────────

/** Default email brand used in mock mode so the editor renders standalone. */
const MOCK_EMAIL_BRAND: EmailBrand & { companyName: string } = {
  logoUrl: null,
  brandColor: '#212733',
  companyName: 'Acme Co',
};

/** List a channel's branded templates + resolved email brand. */
export async function getEmailTemplates(
  channelId: string,
): Promise<EmailTemplatesResponse> {
  if (isMockMode) {
    return mockDelay({
      channelId,
      brand: MOCK_EMAIL_BRAND,
      templates: [
        {
          type: 'AUTO_ACK',
          mode: 'THEMED',
          enabled: true,
          subject: 'Re: {{originalSubject}} [Ticket #{{ticketNumber}}]',
          bodyCopy:
            "Hi {{customerName}},\n\nThanks for contacting us. We've created ticket #{{ticketNumber}} and our team will respond shortly.\n\nBest,\n{{tenantName}}",
          customHtml: null,
        },
        {
          type: 'AGENT_REPLY',
          mode: 'THEMED',
          enabled: true,
          subject: 'Re: {{originalSubject}} [Ticket #{{ticketNumber}}]',
          bodyCopy: '{{message}}\n\n—\n{{tenantName}} Support · Ticket #{{ticketNumber}}',
          customHtml: null,
        },
        {
          type: 'RESOLVED',
          mode: 'THEMED',
          enabled: false,
          subject: 'Your request is resolved [Ticket #{{ticketNumber}}]',
          bodyCopy:
            'Hi {{customerName}},\n\nYour request (ticket #{{ticketNumber}}) has been resolved.\n\n{{resolutionNotes}}\n\nBest,\n{{tenantName}}',
          customHtml: null,
        },
      ],
    });
  }
  const { data } = await apiClient.get<EmailTemplatesResponse>(
    `/engage/channels/${channelId}/email-templates`,
  );
  return data;
}

/** Create/update one template by type. */
export async function upsertEmailTemplate(
  channelId: string,
  type: EmailTemplateType,
  input: UpsertEmailTemplateInput,
) {
  if (isMockMode) return mockDelay({ type, ...input, customHtml: input.customHtml ?? null });
  const { data } = await apiClient.put(
    `/engage/channels/${channelId}/email-templates/${type}`,
    input,
  );
  return data;
}

/** Update the per-channel email brand. */
export async function updateEmailBrand(
  channelId: string,
  patch: EmailBrand,
): Promise<EmailBrand & { companyName: string }> {
  if (isMockMode)
    return mockDelay({
      ...MOCK_EMAIL_BRAND,
      ...patch,
      companyName: patch.companyName ?? MOCK_EMAIL_BRAND.companyName,
    });
  const { data } = await apiClient.patch<EmailBrand & { companyName: string }>(
    `/engage/channels/${channelId}/email-brand`,
    patch,
  );
  return data;
}

/** Upload the email logo (drag-drop). Returns the updated brand. */
export async function uploadEmailBrandLogo(
  channelId: string,
  file: File,
): Promise<EmailBrand & { companyName: string }> {
  if (isMockMode)
    return mockDelay({ ...MOCK_EMAIL_BRAND, logoUrl: URL.createObjectURL(file) });
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await apiClient.post<EmailBrand & { companyName: string }>(
    `/engage/channels/${channelId}/email-brand/logo`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

/** Remove the uploaded logo (reverts to the seeded org logo / name). */
export async function clearEmailBrandLogo(
  channelId: string,
): Promise<EmailBrand & { companyName: string }> {
  if (isMockMode) return mockDelay({ ...MOCK_EMAIL_BRAND, logoUrl: null });
  const { data } = await apiClient.delete<EmailBrand & { companyName: string }>(
    `/engage/channels/${channelId}/email-brand/logo`,
  );
  return data;
}

/** Render a (possibly unsaved) template with sample data for live preview. */
export async function previewEmailTemplate(
  channelId: string,
  input: PreviewEmailTemplateInput,
): Promise<RenderedEmail> {
  if (isMockMode) {
    return mockDelay({
      subject: input.subject.replace(/\{\{[^}]+\}\}/g, 'sample'),
      html: `<html><body style="font-family:sans-serif;padding:24px;"><p>${(input.bodyCopy ?? input.customHtml ?? '').replace(/</g, '&lt;').replace(/\n/g, '<br>')}</p></body></html>`,
      text: input.bodyCopy ?? '',
    });
  }
  const { data } = await apiClient.post<RenderedEmail>(
    `/engage/channels/${channelId}/email-templates/preview`,
    input,
  );
  return data;
}

// ── Tenant-level transactional email templates (invoice, etc.) ──────────────

const MOCK_TX_TEMPLATES: TransactionalEmailTemplate[] = [
  {
    key: 'INVOICE_ISSUED',
    label: 'Invoice issued',
    description: 'Sent to the customer with the tax-invoice PDF attached.',
    tokens: [
      { token: '{{buyerName}}', label: 'Customer name' },
      { token: '{{invoiceNumber}}', label: 'Invoice number' },
      { token: '{{total}}', label: 'Invoice total' },
      { token: '{{dueDate}}', label: 'Due date' },
      { token: '{{sellerName}}', label: 'Your business name' },
    ],
    mode: 'THEMED',
    subject: 'Invoice {{invoiceNumber}} from {{sellerName}}',
    bodyCopy:
      'Hi {{buyerName}},\n\nPlease find attached tax invoice {{invoiceNumber}} for {{total}}, due {{dueDate}}.\n\nThank you,\n{{sellerName}}',
    customHtml: null,
    enabled: true,
    isCustomized: false,
  },
];

export async function listEmailTemplates(): Promise<TransactionalEmailTemplate[]> {
  if (isMockMode) return mockDelay(MOCK_TX_TEMPLATES);
  const { data } = await apiClient.get<TransactionalEmailTemplate[]>('/email/templates');
  return data;
}

export async function previewTransactionalEmail(
  key: string,
  input: PreviewTransactionalEmailInput,
): Promise<RenderedTransactionalEmail> {
  if (isMockMode) {
    return mockDelay({
      subject: input.subject.replace(/\{\{[^}]+\}\}/g, 'sample'),
      html: `<html><body style="font-family:sans-serif;padding:24px;"><p>${(input.bodyCopy ?? '')
        .replace(/</g, '&lt;')
        .replace(/\n/g, '<br>')}</p></body></html>`,
    });
  }
  const { data } = await apiClient.post<RenderedTransactionalEmail>(
    `/email/templates/${key}/preview`,
    input,
  );
  return data;
}

export async function saveTransactionalEmail(
  key: string,
  input: SaveTransactionalEmailInput,
): Promise<TransactionalEmailTemplate> {
  if (isMockMode) {
    return mockDelay({ ...MOCK_TX_TEMPLATES[0], ...input, key, isCustomized: true });
  }
  const { data } = await apiClient.put<TransactionalEmailTemplate>(
    `/email/templates/${key}`,
    input,
  );
  return data;
}

// ─── Notification recipients (account notifications, ADR-045) ─────────────────
const MOCK_NOTIFICATION_RECIPIENTS: NotificationRecipient[] = [
  { id: 'nr-1', email: 'accounts@mavkhouse.com', label: 'Accounts', createdAt: new Date().toISOString() },
];

export async function listNotificationRecipients(): Promise<NotificationRecipient[]> {
  if (isMockMode) return mockDelay(MOCK_NOTIFICATION_RECIPIENTS);
  const { data } = await apiClient.get<NotificationRecipient[]>('/email/notification-recipients');
  return data;
}

export async function addNotificationRecipient(input: {
  email: string;
  label?: string;
}): Promise<NotificationRecipient> {
  if (isMockMode)
    return mockDelay({ id: 'nr-new', email: input.email, label: input.label ?? null, createdAt: new Date().toISOString() });
  const { data } = await apiClient.post<NotificationRecipient>(
    '/email/notification-recipients',
    input,
  );
  return data;
}

export async function removeNotificationRecipient(id: string): Promise<void> {
  if (isMockMode) return mockDelay(undefined);
  await apiClient.delete(`/email/notification-recipients/${id}`);
}

// ─── Sent-email audit log ────────────────────────────────────────────────────
const MOCK_SENT_EMAILS: SentEmailLog[] = [
  {
    id: 'se-1',
    createdAt: new Date(Date.now() - 3600_000).toISOString(),
    recipient: 'tech@mavkhouse.com',
    subject: 'Invoice ZAF-INV-202606-0002 from Saffron Synaptiq Software Lab Private Limited',
    kind: 'INVOICE_ISSUED',
    relatedType: 'kosh_invoice',
    relatedId: 'inv-1',
    relatedRef: 'ZAF-INV-202606-0002',
    messageId: '0109019f-mock',
    status: 'SENT',
    error: null,
  },
  {
    id: 'se-2',
    createdAt: new Date(Date.now() - 90_000_000).toISOString(),
    recipient: 'accounts@example.com',
    subject: 'Reminder: invoice ZAF-INV-202605-0007 is overdue',
    kind: 'INVOICE_REMINDER_OVERDUE',
    relatedType: 'kosh_invoice',
    relatedId: 'inv-2',
    relatedRef: 'ZAF-INV-202605-0007',
    messageId: null,
    status: 'FAILED',
    error: 'SES: Email address is not verified.',
  },
];

export async function listSentEmails(params: SentEmailsQuery = {}): Promise<SentEmailsResult> {
  if (isMockMode) {
    const items = MOCK_SENT_EMAILS.filter(
      (e) =>
        (!params.recipient || e.recipient.toLowerCase().includes(params.recipient.toLowerCase())) &&
        (!params.kind || e.kind === params.kind),
    );
    return mockDelay({ items, page: 1, pageSize: params.pageSize ?? 25, total: items.length });
  }
  const { data } = await apiClient.get<SentEmailsResult>('/email/logs', { params });
  return data;
}

// ─── Internal assistant ────────────────────────────────────────────────────
export async function askAssistant(
  message: string,
  history: AssistantMessage[],
): Promise<AssistantReply> {
  if (isMockMode) return mockDelay({ reply: mockAssistantReply(message) });
  const { data } = await apiClient.post<AssistantReply>(
    '/engage/assistant/messages',
    {
      message,
      history: history.map((m) => ({ role: m.role, content: m.content })),
    },
    // A local Ollama model can take tens of seconds to generate.
    { timeout: 120_000 },
  );
  return data;
}

// ─── Bot API (customer-facing chatbot) ─────────────────────────────────────

/** In-mock-mode store so create/update/delete persist for the session. */
let mockBotKnowledge: BotKnowledge[] = [...fixtureBotKnowledge];

export async function listBotKnowledge(): Promise<BotKnowledge[]> {
  if (isMockMode) return mockDelay([...mockBotKnowledge]);
  const { data } = await apiClient.get<BotKnowledge[]>('/engage/bot-api/knowledge');
  return data;
}

/** The tenant's registered widget domains — drives the per-domain link editor. */
export async function getBotDomains(): Promise<BotDomains> {
  if (isMockMode) return mockDelay({ primary: null, domains: [] });
  const { data } = await apiClient.get<BotDomains>('/engage/bot-api/domains');
  return data;
}

const DEFAULT_BOT_CONFIG: BotConfig = {
  botName: 'Zara',
  greeting: 'Hi! How can we help today?',
  headerTitle: null,
  primaryColor: '#ff7a1a',
  headerColor: null,
  headerTextColor: '#ffffff',
  mode: 'LIGHT',
  position: 'bottom-right',
  cornerRadius: 16,
  avatarUrl: null,
  brandingMode: 'BOTH',
  avatarShape: 'ROUND',
  botMode: 'SUPPORT',
  systemInstructions: null,
  leadCtaLabel: null,
  leadEmail: null,
  leadWebhookUrl: null,
  leadWebhookSecret: null,
  forwardIntakeKey: null,
  leadIntakeKey: null,
};

/** Chatbot widget display + theme config (Appearance editor). */
export async function getBotConfig(): Promise<BotConfig> {
  if (isMockMode) return mockDelay({ ...DEFAULT_BOT_CONFIG });
  const { data } = await apiClient.get<BotConfig>('/engage/bot-api/config');
  return data;
}

export async function saveBotConfig(patch: Partial<BotConfig>): Promise<BotConfig> {
  if (isMockMode) return mockDelay({ ...DEFAULT_BOT_CONFIG, ...patch });
  const { data } = await apiClient.put<BotConfig>('/engage/bot-api/config', patch);
  return data;
}

/** Get-or-create (or rotate) this tenant's lead intake key — share it so
 *  another tenant can forward their captured leads into our inbox. */
export async function ensureLeadIntakeKey(rotate = false): Promise<{ leadIntakeKey: string }> {
  if (isMockMode) return mockDelay({ leadIntakeKey: 'zlk_mock0000000000000000000000000000' });
  const { data } = await apiClient.post<{ leadIntakeKey: string }>(
    `/engage/bot-api/lead-intake-key${rotate ? '?rotate=1' : ''}`,
  );
  return data;
}

export async function createBotKnowledge(input: {
  title: string;
  content: string;
  category?: string;
  /**
   * Stable key from the KB onboarding checklist — set when this entry is the
   * answer to one of the curated onboarding prompts. The runtime persists it
   * so the checklist can look up its entry on subsequent renders.
   */
  onboardingKey?: string;
  links?: BotKnowledge['links'];
  showOnPortal?: boolean;
  portalSortOrder?: number;
}): Promise<BotKnowledge> {
  if (isMockMode) {
    const now = new Date().toISOString();
    const entry: BotKnowledge = {
      id: `bk_${Math.random().toString(36).slice(2, 8)}`,
      title: input.title,
      content: input.content,
      category: input.category,
      onboardingKey: input.onboardingKey ?? null,
      status: 'draft',
      createdAt: now,
      updatedAt: now,
    };
    mockBotKnowledge = [entry, ...mockBotKnowledge];
    return mockDelay(entry);
  }
  // Send title/content plus category and onboardingKey when set — the runtime
  // ignores absent fields, so omitting them keeps older callers compatible.
  const payload: {
    title: string;
    content: string;
    category?: string;
    onboardingKey?: string;
    links?: BotKnowledge['links'];
    showOnPortal?: boolean;
    portalSortOrder?: number;
  } = {
    title: input.title,
    content: input.content,
  };
  if (input.category !== undefined) payload.category = input.category;
  if (input.onboardingKey !== undefined)
    payload.onboardingKey = input.onboardingKey;
  if (input.links !== undefined) payload.links = input.links;
  if (input.showOnPortal !== undefined) payload.showOnPortal = input.showOnPortal;
  if (input.portalSortOrder !== undefined)
    payload.portalSortOrder = input.portalSortOrder;
  const { data } = await apiClient.post<BotKnowledge>(
    '/engage/bot-api/knowledge',
    payload,
  );
  return data;
}

export async function updateBotKnowledge(
  id: string,
  patch: {
    title?: string;
    content?: string;
    category?: string;
    status?: BotKnowledge['status'];
    links?: BotKnowledge['links'];
    showOnPortal?: boolean;
    portalSortOrder?: number;
  },
): Promise<BotKnowledge> {
  if (isMockMode) {
    const now = new Date().toISOString();
    let updated: BotKnowledge | undefined;
    mockBotKnowledge = mockBotKnowledge.map((k) => {
      if (k.id !== id) return k;
      updated = { ...k, ...patch, updatedAt: now };
      return updated;
    });
    return mockDelay(updated ?? mockBotKnowledge[0]);
  }
  const { data } = await apiClient.patch<BotKnowledge>(
    `/engage/bot-api/knowledge/${id}`,
    patch,
  );
  return data;
}

export async function approveBotKnowledge(id: string): Promise<BotKnowledge> {
  if (isMockMode) {
    const now = new Date().toISOString();
    let updated: BotKnowledge | undefined;
    mockBotKnowledge = mockBotKnowledge.map((k) => {
      if (k.id !== id) return k;
      updated = {
        ...k,
        status: 'approved',
        approvedBy: 'You',
        approvedAt: now,
        updatedAt: now,
      };
      return updated;
    });
    return mockDelay(updated ?? mockBotKnowledge[0]);
  }
  const { data } = await apiClient.post<BotKnowledge>(
    `/engage/bot-api/knowledge/${id}/approve`,
  );
  return data;
}

export async function deleteBotKnowledge(id: string): Promise<void> {
  if (isMockMode) {
    mockBotKnowledge = mockBotKnowledge.filter((k) => k.id !== id);
    return mockDelay(undefined);
  }
  await apiClient.delete(`/engage/bot-api/knowledge/${id}`);
}

export async function previewBot(
  message: string,
  history: Array<{ role: 'user' | 'assistant'; content: string }>,
  customerId?: string,
): Promise<BotPreviewReply> {
  if (isMockMode) return mockDelay(mockBotPreviewReply(message, customerId));
  const { data } = await apiClient.post<BotPreviewReply>(
    '/engage/bot-api/preview',
    // `customerId` grounds the bot on that customer's 360 — only sent when set.
    { message, history, ...(customerId ? { customerId } : {}) },
    // The grounded bot may call a local model — allow it time to respond.
    { timeout: 120_000 },
  );
  // The runtime now returns contextual quick-replies; older builds may not.
  return { ...data, suggestions: data.suggestions ?? [] };
}

/**
 * Knowledge gaps — clusters of customer questions the bot could not answer,
 * surfaced so the tenant can fill the gap. Live: `GET /engage/bot-api/gaps`.
 * The runtime runs an LLM clustering pass to build these, so it is SLOW
 * (~10–20s) — callers should trigger it on demand, never auto-load.
 */
export async function getBotGaps(): Promise<BotKnowledgeGap[]> {
  if (isMockMode) return mockDelay(mockBotGaps);
  const { data } = await apiClient.get<BotKnowledgeGap[]>(
    '/engage/bot-api/gaps',
    // The clustering pass calls a model — give it room to finish.
    { timeout: 120_000 },
  );
  return data;
}

/**
 * Restore a customer's recent bot-chat history so the widget can re-seed
 * the thread on open. Live: `GET /engage/bot-api/conversation` with
 * `customerId` (+ optional `limit`) as query params. Empty state is a
 * normal response with `conversationId: null` and `messages: []`.
 */
export async function getBotConversation(
  customerId: string,
  limit = 20,
): Promise<BotConversationHistory> {
  if (isMockMode) return mockDelay(mockBotConversation(customerId, limit));
  const { data } = await apiClient.get<BotConversationHistory>(
    '/engage/bot-api/conversation',
    { params: { customerId, limit } },
  );
  return data;
}

/**
 * Plan + usage snapshot for the Engage chatbot. Live:
 * `GET /engage/bot-api/usage`. Soft-warn — the bot never blocks; this is
 * purely informational. Returned as the apiClient-unwrapped envelope payload.
 */
export async function getBotUsage(): Promise<BotUsage> {
  if (isMockMode) return mockDelay(mockBotUsage);
  const { data } = await apiClient.get<BotUsage>('/engage/bot-api/usage');
  return data;
}

/** Standing actions always offered in the bot's opening menu. */
const BOT_INTRO_ACTIONS = ['Track an order', 'Raise a request'];

/**
 * The bot's opening quick-reply menu. Live: `GET /engage/bot-api/intro`.
 * Mock: derive topic chips from the distinct categories of APPROVED knowledge
 * entries, then append the standing actions.
 */
export async function getBotIntro(): Promise<BotIntro> {
  if (isMockMode) {
    const categories = [
      ...new Set(
        fixtureBotKnowledge
          .filter((k) => k.status === 'approved' && k.category)
          .map((k) => k.category as string),
      ),
    ];
    return mockDelay({ suggestions: [...categories, ...BOT_INTRO_ACTIONS] });
  }
  const { data } = await apiClient.get<BotIntro>('/engage/bot-api/intro');
  return { suggestions: data.suggestions ?? [] };
}
